import { createSelectors } from '@src/stores/create-selectors';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

export type RefreshAccountJobStatus =
  'init' | 'pending' | 'complete' | 'failed';

export type RefreshAccountJob = {
  readonly accountId: string;
  readonly bankName: string;
  isExternal?: boolean;
  id: string;
  status: RefreshAccountJobStatus;
  readonly createdAt: number;
};

type State = {
  refreshAccountJobs: Record<string, RefreshAccountJob>;
  /** Accounts whose latest refresh failed, keyed by accountId (value: failedAt).
   * Kept after a batch finishes so cards can show the failure, until the
   * account's balance is updated after failedAt. */
  failedRefreshes: Record<string, number>;
  jobsCompleted: boolean;
  actions: Actions;
};

export type AddRefreshAccountJobPayload = {
  readonly accountId: string;
  readonly bankName: string;
  isExternal?: boolean;
};

type UpdateJobStatusPayload = {
  readonly accountId: string;
  readonly status: RefreshAccountJobStatus;
};

type UpdateJobIdPayload = {
  readonly accountId: string;
  readonly id: string;
};

type Actions = {
  addRefreshAccountJob: ({
    accountId,
    bankName,
    isExternal,
  }: AddRefreshAccountJobPayload) => void;
  removeAllRefreshAccountJobs: () => void;
  updateRefreshAccountJobStatus: ({
    accountId,
    status,
  }: UpdateJobStatusPayload) => void;
  updateRefreshAccountJobId: ({ accountId, id }: UpdateJobIdPayload) => void;
  markJobsCompleted: () => void;
};

const useRefreshAccountJobsStoreBase = create<State>()(
  immer(
    devtools(
      (set) => ({
        refreshAccountJobs: {},
        failedRefreshes: {},
        jobsCompleted: true,
        actions: {
          addRefreshAccountJob: ({
            accountId,
            bankName,
            isExternal = false,
          }: AddRefreshAccountJobPayload) =>
            set(
              (state) => {
                // A finished job can be retried before the batch clears;
                // an init or pending one is already running
                const existing = state.refreshAccountJobs[accountId];
                if (
                  !existing ||
                  existing.status === 'failed' ||
                  existing.status === 'complete'
                ) {
                  delete state.failedRefreshes[accountId];
                  state.jobsCompleted = false;
                  state.refreshAccountJobs[accountId] = {
                    accountId,
                    bankName,
                    isExternal,
                    id: '',
                    status: 'init',
                    createdAt: Date.now(),
                  };
                }
              },
              false,
              'addRefreshAccountJob',
            ),
          removeAllRefreshAccountJobs: () =>
            set(
              (state) => {
                state.refreshAccountJobs = {};
              },
              false,
              'removeAllRefreshAccountJobs',
            ),
          updateRefreshAccountJobStatus: ({
            accountId,
            status,
          }: UpdateJobStatusPayload) =>
            set(
              (state) => {
                const job = state.refreshAccountJobs[accountId];
                if (job) {
                  job.status = status;
                  if (status === 'failed') {
                    state.failedRefreshes[accountId] = Date.now();
                  } else if (status === 'complete') {
                    delete state.failedRefreshes[accountId];
                  }
                }
              },
              false,
              'updateRefreshAccountJobStatus',
            ),
          updateRefreshAccountJobId: ({ accountId, id }) =>
            set(
              (state) => {
                const job = state.refreshAccountJobs[accountId];
                if (job) {
                  job.id = id;
                }
              },
              false,
              'updateRefreshAccountJobId',
            ),
          markJobsCompleted: () =>
            set(
              (state) => {
                state.jobsCompleted = true;
              },
              false,
              'markJobsCompleted',
            ),
        },
      }),
      { name: 'refreshAccountJobs' },
    ),
  ),
);

export const useRefreshAccountJobsStore = createSelectors(
  useRefreshAccountJobsStoreBase,
);
